import { sqlIn } from '../utils/sql-in.js';
import { controlSources, standingOf } from './control-source-service.js';
import { contestedRulesForWorkspace } from './system-applicability-service.js';
import { loadRuleChoices } from './workspace-rule-applicability-service.js';
/** Register risks that only track the legal patchwork: one row, "covered by the controls below" (ADR-0114 §3). */
export const LEGAL_TRACKING_RISK_IDS = new Set(['RR-026']);
/** States a gap is shown for (the control is not demonstrably passing). */
const GAP_STATES = new Set(['fail', 'no_evidence', 'insufficient', 'stale', 'couldnt_check']);
const PASS_STATES = new Set(['pass', 'excepted']);
export const PRIVILEGE_INCLUSION_REASONS = ['insurer_request', 'renewal_underwriting', 'claim_support', 'broker_request'];
const parse = (s) => {
    if (!s)
        return null;
    try {
        return JSON.parse(s);
    }
    catch {
        return null;
    }
};
const missingTable = (err) => /no such table|does not exist/i.test(err instanceof Error ? err.message : String(err));
export async function buildLegalSection(db, contentDb, input) {
    const ws = input.workspace_id;
    const today = input.now.toISOString().slice(0, 10);
    // 1. Rules in scope: current obligations (applies), contested rules a system would apply, and rules the workspace
    //    chose do not apply. Rationale and systems come from the current obligation rows.
    const obligations = (await db.query(`SELECT o.rule_id, o.system_id, o.rationale, o.obligation_json FROM system_obligations o
       JOIN ai_systems s ON s.system_id = o.system_id
      WHERE s.workspace_id = ? AND o.superseded_at IS NULL AND o.rule_id IS NOT NULL
      ORDER BY o.rule_id, o.system_id`, [ws])).results;
    const systemsByRule = new Map();
    const triggerByRule = new Map();
    const requiredByRule = new Map();
    for (const o of obligations) {
        const list = systemsByRule.get(o.rule_id) ?? [];
        if (!list.includes(o.system_id))
            list.push(o.system_id);
        systemsByRule.set(o.rule_id, list);
        if (o.rationale && !triggerByRule.has(o.rule_id))
            triggerByRule.set(o.rule_id, o.rationale);
        const req = parse(o.obligation_json)?.required_controls ?? [];
        requiredByRule.set(o.rule_id, new Set([...(requiredByRule.get(o.rule_id) ?? []), ...req]));
    }
    const choices = await loadRuleChoices(db, ws);
    const contested = await contestedRulesForWorkspace(db, ws).catch(() => new Map());
    const ruleIds = [...new Set([...systemsByRule.keys(), ...contested.keys(), ...Object.keys(choices)])].sort();
    const rules = new Map();
    if (ruleIds.length) {
        const inList = sqlIn(ruleIds);
        for (const r of (await contentDb.query(`SELECT rule_id, statute, citation, effective_date, legislation_id, obligation_json, source_refs_json
         FROM jurisdiction_rules WHERE status = 'active' AND rule_id IN ${inList.sql}`, inList.params)).results)
            rules.set(r.rule_id, r);
    }
    for (const r of rules.values()) {
        if (requiredByRule.has(r.rule_id))
            continue;
        requiredByRule.set(r.rule_id, new Set(parse(r.obligation_json)?.required_controls ?? []));
    }
    const legIds = [...new Set([...rules.values()].map((r) => r.legislation_id).filter((x) => !!x))];
    const laws = new Map();
    if (legIds.length) {
        const inList = sqlIn(legIds);
        for (const l of (await contentDb.query(`SELECT legislation_id, short_title, citation, source_url, effective_date, legal_status, stay_kind
         FROM legislation WHERE legislation_id IN ${inList.sql}`, inList.params)).results)
            laws.set(l.legislation_id, l);
    }
    // 2. Controls: required by any rule in scope, plus the credited ones.
    const lawControlIds = new Set([...requiredByRule.values()].flatMap((s) => [...s]));
    const controlIds = [...new Set([...lawControlIds, ...input.creditedControlIds])].sort();
    const sources = controlIds.length ? await controlSources(db, controlIds, { workspaceId: ws, today }) : new Map();
    const states = controlIds.length ? await input.states(controlIds) : new Map();
    const titles = new Map();
    if (controlIds.length) {
        const inList = sqlIn(controlIds);
        for (const c of (await contentDb.query(`SELECT control_id, title FROM controls WHERE control_id IN ${inList.sql}`, inList.params)).results)
            titles.set(c.control_id, c.title);
    }
    // 3. Law rows, grouped by law (legislation id, else rule id).
    const lawRows = new Map();
    for (const ruleId of ruleIds) {
        const rule = rules.get(ruleId);
        if (!rule)
            continue; // withdrawn rule: the obligation row is stale
        const law = rule.legislation_id ? laws.get(rule.legislation_id) ?? null : null;
        const lawId = rule.legislation_id ?? rule.rule_id;
        const standing = standingOf(law?.legal_status ?? null, law?.effective_date ?? rule.effective_date, today);
        const applicability = choices[ruleId] ?? (systemsByRule.has(ruleId) ? 'applies' : 'decision_needed');
        const struck = standing === 'vacated' || standing === 'annulled';
        const refUrl = parse(rule.source_refs_json)?.find((r) => r.url)?.url ?? null;
        const row = lawRows.get(lawId) ?? {
            law_id: lawId,
            legislation_id: rule.legislation_id,
            rule_ids: [],
            short_name: law?.short_title ?? rule.statute,
            citation: law?.citation ?? rule.citation,
            url: law?.source_url ?? refUrl,
            trigger: null,
            effective_date: law?.effective_date ?? rule.effective_date,
            standing,
            stay_kind: standing === 'stayed' ? (law?.stay_kind ?? null) : null,
            applicability,
            counted: false,
            not_counted_reason: null,
            system_ids: [],
            controls: { passing: 0, of: 0, control_ids: [] },
        };
        row.rule_ids.push(ruleId);
        row.trigger ??= triggerByRule.get(ruleId) ?? null;
        // One rule that applies makes the law apply.
        if (applicability === 'applies')
            row.applicability = 'applies';
        else if (row.applicability !== 'applies' && applicability === 'decision_needed')
            row.applicability = 'decision_needed';
        for (const s of systemsByRule.get(ruleId) ?? contested.get(ruleId) ?? [])
            if (!row.system_ids.includes(s))
                row.system_ids.push(s);
        for (const c of requiredByRule.get(ruleId) ?? [])
            if (!row.controls.control_ids.includes(c))
                row.controls.control_ids.push(c);
        if (row.effective_date && rule.effective_date && rule.effective_date < row.effective_date && !law)
            row.effective_date = rule.effective_date;
        row.counted = row.applicability === 'applies' && !struck;
        row.not_counted_reason = row.counted ? null
            : struck ? 'struck'
                : row.applicability === 'decision_needed' ? 'decision_needed'
                    : standing === 'stayed' ? 'paused'
                        : 'does_not_apply';
        lawRows.set(lawId, row);
    }
    for (const row of lawRows.values()) {
        row.controls.control_ids.sort();
        row.system_ids.sort();
        row.controls.of = row.controls.control_ids.length;
        row.controls.passing = row.controls.control_ids.filter((c) => PASS_STATES.has(states.get(c)?.state ?? '')).length;
    }
    // 4. Privileged determinations on the laws in scope: withheld unless included for this pack.
    let withheld = 0;
    const included = new Set(input.includePrivileged?.determination_ids ?? []);
    try {
        const dets = (await db.query(`SELECT determination_id, system_id, rule_ids_json, outcome, reason, visibility, privileged
         FROM applicability_determinations
        WHERE workspace_id = ? AND state IN ('decided', 'recorded') ORDER BY determination_id`, [ws])).results;
        for (const d of dets) {
            const ids = new Set(parse(d.rule_ids_json) ?? []);
            const row = [...lawRows.values()].find((l) => l.rule_ids.some((r) => ids.has(r)));
            if (!row)
                continue;
            if (d.privileged === 1 && d.visibility !== 'external' && !included.has(d.determination_id)) {
                withheld += 1;
                continue;
            }
            if (d.privileged === 1 || d.visibility === 'external') {
                (row.determinations ??= []).push({ determination_id: d.determination_id, system_id: d.system_id, outcome: d.outcome, reason: d.reason });
            }
        }
    }
    catch (err) {
        if (!missingTable(err))
            throw err;
    }
    // 5. Gaps: control owner + earliest open finding deadline.
    const gapIds = controlIds.filter((c) => GAP_STATES.has(states.get(c)?.state ?? 'no_evidence'));
    const owners = new Map();
    const fixBy = new Map();
    if (gapIds.length) {
        const inList = sqlIn(gapIds);
        for (const a of (await db.query(`SELECT a.subject_id, u.display_name FROM assignments a JOIN users u ON u.user_id = a.user_id
        WHERE a.workspace_id = ? AND a.subject_kind = 'control' AND a.responsibility = 'owner' AND a.effective_to IS NULL
          AND a.subject_id IN ${inList.sql} ORDER BY a.effective_from DESC`, [ws, ...inList.params])).results)
            if (!owners.has(a.subject_id))
                owners.set(a.subject_id, a.display_name);
        for (const f of (await db.query(`SELECT control_id, MIN(sla_deadline_at) AS due FROM findings
        WHERE workspace_id = ? AND status IN ('open', 'acknowledged') AND control_id IN ${inList.sql}
        GROUP BY control_id`, [ws, ...inList.params])).results)
            if (f.due)
                fixBy.set(f.control_id, f.due);
    }
    // 6. Control rows.
    const systemsByControl = new Map();
    for (const l of lawRows.values()) {
        for (const c of l.controls.control_ids) {
            const set = systemsByControl.get(c) ?? new Set();
            for (const s of l.system_ids)
                set.add(s);
            systemsByControl.set(c, set);
        }
    }
    const rows = controlIds.map((cid) => {
        const src = sources.get(cid) ?? [];
        const lawSrc = src.filter((s) => s.kind === 'law');
        // Framework short names, as the tag line shows them (e.g. "NIST AI RMF").
        const fw = src.filter((s) => s.kind === 'framework').map((s) => s.short_name);
        const cases = src.filter((s) => s.kind === 'case' && s.counts).length;
        const tagLaws = new Map();
        for (const l of lawSrc) {
            const id = l.legislation_id ?? l.rule_id;
            const prev = tagLaws.get(id);
            tagLaws.set(id, { law_id: id, short_name: l.short_name, counted: (prev?.counted ?? false) || l.counts });
        }
        const st = states.get(cid);
        const state = st?.state ?? 'no_evidence';
        const countedLaw = [...tagLaws.values()].some((l) => l.counted);
        const struckOnly = lawSrc.length > 0 && lawSrc.every((l) => l.standing === 'vacated' || l.standing === 'annulled');
        return {
            control_id: cid,
            title: titles.get(cid) ?? null,
            state,
            observed_at: st?.observed_at ?? null,
            tags: {
                laws: [...tagLaws.values()].sort((a, b) => Number(b.counted) - Number(a.counted) || a.short_name.localeCompare(b.short_name)),
                frameworks: fw,
                cases,
            },
            no_current_legal_source: struckOnly && fw.length === 0,
            counted: countedLaw || (fw.length > 0 && input.creditedControlIds.includes(cid)),
            system_ids: [...(systemsByControl.get(cid) ?? [])].sort(),
            gap: GAP_STATES.has(state)
                ? { owner_role: 'Control owner', owner_name: owners.get(cid) ?? null, fix_by: fixBy.get(cid) ?? null }
                : null,
        };
    });
    rows.sort((a, b) => Number(b.counted) - Number(a.counted) || a.control_id.localeCompare(b.control_id));
    const lawList = [...lawRows.values()].sort((a, b) => Number(b.counted) - Number(a.counted) || a.short_name.localeCompare(b.short_name));
    const counted = rows.filter((r) => r.counted);
    return {
        laws_in_scope: lawList.length,
        counted_laws: lawList.filter((l) => l.counted).length,
        in_force_laws: lawList.filter((l) => l.counted && l.standing === 'in_force').length,
        controls: counted.length,
        controls_passing: counted.filter((r) => PASS_STATES.has(r.state)).length,
        rows,
        laws: lawList,
        tracking: [],
        withheld_privileged: withheld,
    };
}
const rank = (s) => (PASS_STATES.has(s) ? 2 : s === 'fail' ? 0 : 1);
export function legalChanges(current, previous) {
    if (!previous)
        return { previous_version: null, comparable: true, performance: [], counting: [] };
    if (!previous.legal)
        return { previous_version: previous.version, comparable: false, performance: [], counting: [] };
    const prevRows = new Map(previous.legal.rows.map((r) => [r.control_id, r]));
    const curRows = new Map(current.rows.map((r) => [r.control_id, r]));
    const prevLaws = new Map(previous.legal.laws.map((l) => [l.law_id, l]));
    const curLaws = new Map(current.laws.map((l) => [l.law_id, l]));
    const counting = [];
    for (const [id, l] of curLaws) {
        const p = prevLaws.get(id);
        if (!p)
            counting.push({ kind: 'law_added', id, label: l.short_name });
        else if (p.counted !== l.counted)
            counting.push({ kind: l.counted ? 'law_counted' : 'law_not_counted', id, label: l.short_name });
    }
    for (const [id, p] of prevLaws)
        if (!curLaws.has(id))
            counting.push({ kind: 'law_removed', id, label: p.short_name });
    for (const [id, r] of curRows)
        if (!prevRows.has(id))
            counting.push({ kind: 'control_added', id, label: r.title ?? id });
    for (const [id, p] of prevRows)
        if (!curRows.has(id))
            counting.push({ kind: 'control_removed', id, label: p.title ?? id });
    const performance = [];
    for (const [id, r] of curRows) {
        const p = prevRows.get(id);
        if (!p || p.state === r.state || rank(p.state) === rank(r.state))
            continue;
        performance.push({ control_id: id, from: p.state, to: r.state, direction: rank(r.state) > rank(p.state) ? 'better' : 'worse' });
    }
    return { previous_version: previous.version, comparable: true, performance, counting };
}
// ---------------------------------------------------------------------------
// Verify projection: what a keyed verify viewer sees of the legal section.
// ---------------------------------------------------------------------------
/** Drops internal-only fields: owner names and the withheld-privilege note (owner decision 2026-10-26). */
export function legalForVerify(section) {
    const { withheld_privileged: _withheld, ...rest } = section;
    return {
        ...rest,
        rows: rest.rows.map((r) => ({ ...r, gap: r.gap ? { owner_role: r.gap.owner_role, owner_name: null, fix_by: r.gap.fix_by } : null })),
    };
}
//# sourceMappingURL=insurability-pack-legal-service.js.map